import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { Steps, Tag, Typography } from '@pankod/refine-antd'

import {
  TCertificateTTEGetSubmissionResponse,
  TCertificateTTEWorkflow,
} from '../types'

dayjs.extend(utc)
dayjs.extend(timezone)

const { Text } = Typography

interface TApprovalWorkflowProps {
  pengajuan: TCertificateTTEGetSubmissionResponse
  variant?: 'admin' | 'signer'
}

export const ApprovalWorkflow: React.FC<TApprovalWorkflowProps> = ({
  pengajuan,
  variant = 'admin',
}) => {
  const { workflows, currentWorkflowIndex, status } = pengajuan

  if (!workflows || workflows.length === 0) {
    return <Text type="secondary">Tidak ada riwayat persetujuan.</Text>
  }

  const isRejected = status === 'ditolak'
  const isComplete = status === 'selesai'
  const isTtdUlang = status === 'tte_ulang'

  const getStepStatus = (
    approver: TCertificateTTEWorkflow,
    idx: number,
  ): 'wait' | 'process' | 'finish' | 'error' => {
    if (approver.status === 'ditolak') return 'error'

    // For tte_ulang, if it's the signer that needs to re-sign
    if (
      isTtdUlang &&
      approver.role === 'signer' &&
      idx === currentWorkflowIndex
    )
      return 'process'
    if (isTtdUlang && approver.role === 'signer' && idx > currentWorkflowIndex)
      return 'wait'

    if (idx === currentWorkflowIndex && !isRejected && !isComplete) {
      if (approver.status === 'menunggu_persetujuan') return 'wait'
      if (approver.status === 'dalam_proses') return 'finish'
      return 'process'
    }

    if (
      approver.status === 'selesai' ||
      approver.action === 'approve' ||
      approver.action === 'reject' ||
      approver.actionedAt != null
    ) {
      return 'finish'
    }

    return 'wait'
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Text strong style={{ display: 'block', marginBottom: 12 }}>
        Riwayat Persetujuan
      </Text>

      {/* Approver steps */}
      <Steps direction="vertical" size="small" style={{ flex: 1 }}>
        {workflows.map((approver, idx) => {
          const stepStatus = getStepStatus(approver, idx)
          const actionLabel =
            approver.role === 'approver'
              ? 'Pemberi Persetujuan'
              : 'Penandatangan'

          const descriptionParts: React.ReactNode[] = [
            <span key="jabatan" style={{ display: 'block', fontSize: 12 }}>
              {approver.jabatan}
            </span>,
            <span
              key="action"
              style={{ display: 'block', fontSize: 11, color: '#8c8c8c' }}
            >
              {actionLabel}
              {approver.actionedAt
                ? ` · ${dayjs(approver.actionedAt)
                    .tz('Asia/Jakarta')
                    .format('YYYY-MM-DD HH:mm [(GMT+7)]')}`
                : ''}
            </span>,
          ]

          if (approver.statusReason) {
            descriptionParts.push(
              <span
                key="alasan"
                style={{
                  display: 'block',
                  color: '#cf1322',
                  fontStyle: 'italic',
                  marginTop: 2,
                }}
              >
                Alasan: {approver.statusReason}
              </span>,
            )
          }

          return (
            <Steps.Step
              key={approver.id || idx}
              status={stepStatus}
              title={
                <span style={{ fontSize: 13 }}>
                  {approver.nama}
                  {idx === currentWorkflowIndex && (
                    <Tag
                      color={
                        ['siap_diproses', 'tte_ulang'].includes(approver.status)
                          ? 'processing'
                          : 'default'
                      }
                      className="!ml-2"
                    >
                      {variant === 'signer' ? 'Anda' : 'Tahap Aktif'}
                    </Tag>
                  )}
                </span>
              }
              description={<div>{descriptionParts}</div>}
            />
          )
        })}
      </Steps>
    </div>
  )
}
